/**
 * Generate N JPEG thumbnails from a video URL by seeking to evenly-spaced
 * timestamps and drawing the current frame to a canvas. Returns base64 data
 * URLs ready to be sent inline to the API.
 *
 * Notes:
 * - Requires the source to be same-origin or CORS-enabled (our S3 bucket is).
 * - We downscale to maxSide to keep payloads small.
 */
export async function generateVideoThumbnails(
  url: string,
  opts: { count?: number; maxSide?: number; quality?: number } = {},
): Promise<string[]> {
  const count = opts.count ?? 3;
  const maxSide = opts.maxSide ?? 720;
  const quality = opts.quality ?? 0.82;

  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.src = url;

  await new Promise<void>((resolve, reject) => {
    const onLoaded = () => { cleanup(); resolve(); };
    const onErr = () => { cleanup(); reject(new Error('No se pudo cargar el vídeo para generar miniaturas.')); };
    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('error', onErr);
    };
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('error', onErr);
  });

  const duration = isFinite(video.duration) && video.duration > 0 ? video.duration : 1;
  const fractions: number[] = [];
  for (let i = 1; i <= count; i++) fractions.push(i / (count + 1));
  const timestamps = fractions.map((f) => Math.min(duration - 0.05, Math.max(0.05, duration * f)));

  const sw = video.videoWidth || 720;
  const sh = video.videoHeight || 720;
  const scale = Math.min(1, maxSide / Math.max(sw, sh));
  const dw = Math.max(1, Math.round(sw * scale));
  const dh = Math.max(1, Math.round(sh * scale));

  const canvas = document.createElement('canvas');
  canvas.width = dw;
  canvas.height = dh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D no disponible.');

  const thumbnails: string[] = [];
  for (const t of timestamps) {
    await seekVideo(video, t);
    ctx.drawImage(video, 0, 0, dw, dh);
    thumbnails.push(canvas.toDataURL('image/jpeg', quality));
  }
  return thumbnails;
}

function seekVideo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const onSeeked = () => { cleanup(); resolve(); };
    const onErr = () => { cleanup(); reject(new Error('Error al saltar al frame del vídeo.')); };
    const cleanup = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onErr);
    };
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onErr);
    try {
      video.currentTime = time;
    } catch (err) {
      cleanup();
      reject(err instanceof Error ? err : new Error('seek failed'));
    }
  });
}
